import { BellIcon } from '@phosphor-icons/react';
import { Dropdown } from 'antd';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import defaultAvatar from '@/assets/images/profile-avatar.webp';
import { FieldProLogo } from '@/components/common/field-pro-logo';
import { UserAvatar } from '@/components/common/user-avatar';
import { useConfirm, WarningDiamondIcon } from '@/components/feedback/dialog';
import { nav } from '@/config/nav';
import { routes } from '@/config/routes';
import { useLogout } from '@/features/auth';
import { useDevicesUiStore } from '@/features/devices';
import {
  NotificationSettingsDrawer,
  NotificationsPanel,
  useUnreadCount,
} from '@/features/notifications';
import {
  ChangePasswordFlow,
  ContactUsModal,
  EditProfileModal,
  PROFILE_COPY,
  ProfileMenu,
  type ProfilePanel,
} from '@/features/profile';
import { WarrantyClaimsDrawer } from '@/features/warranty';
import { useAuth } from '@/providers/auth-provider';

import styles from './top-nav.module.scss';

export type TopNavProps = {
  /**
   * Callback ref for the per-page header slot. `<AppShell>` holds the element in state and
   * publishes it through `HeaderSlotContext`; pages fill it with `<HeaderSlot>`. The slot is
   * empty — and takes no gap — unless a page opts in.
   */
  slotRef?: (el: HTMLDivElement | null) => void;
  /**
   * D-01: a map surface (`handle: { fullBleed: true }`) floats this bar directly over an
   * edge-to-edge canvas instead of a cream page — `<AppShell>` decides this per-route. Drops the
   * decorative texture band (there is no cream backdrop left to paint it on) and lets clicks/drags
   * outside the pill itself reach the map beneath.
   */
  floating?: boolean;
};

/**
 * The FieldPro top app bar (Figma node 232:350874 — the same bar on every frame of US-112439 /
 * US-112437).
 *
 * A dark `brand-950` pill inset 48px from the page edges and 27px from the top, sitting on the
 * cream page background. Three regions: the flat primary nav on the left (text only, the active
 * item in `accent2-500` with a 2px underline), the FIELDPRO wordmark **absolutely centred** on the
 * viewport — Figma puts it at dead centre regardless of how wide the side clusters are — and a
 * right cluster of the per-page header slot, notifications and profile.
 *
 * Both right-hand controls open **dropdown panels**, not pages (Figma 621:549480, 626:582182).
 * The notification story describes a full "Notifications screen" reached from the bell, which is
 * its mobile shape; on web the same feed is this popover.
 *
 * Layout chrome, so it legitimately consumes the auth, notifications and profile features'
 * public surfaces.
 */
export function TopNav({ slotRef, floating = false }: TopNavProps) {
  const navigate = useNavigate();
  const { user, clearSession } = useAuth();
  const logout = useLogout();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [editing, setEditing] = useState<ProfilePanel['identity'] | null>(null);

  // The bell's unread badge. US-101849 § ENTRY POINT specifies a dot with no number; the count is
  // shown anyway on explicit request, matching Tracking Mobile's bell. `99+` caps it so a long
  // backlog cannot widen the nav pill. The dot remains the fallback for a "something is unread but
  // we have no number" answer, which is what `/unread` alone can give.
  const { count, hasUnread } = useUnreadCount();
  const countLabel = count > 99 ? '99+' : String(count);

  const { confirm } = useConfirm();

  const signOut = () => {
    setProfileOpen(false);
    void confirm({
      icon: <WarningDiamondIcon />,
      title: PROFILE_COPY.LOGOUT_TITLE,
      subtitle: PROFILE_COPY.LOGOUT_BODY,
      confirmText: PROFILE_COPY.LOGOUT_CONFIRM,
      fillActions: true,
      // The dialog shows loading until this settles, then closes either way. A failure keeps the
      // session: <MutationToaster> has already raised the warning (US-117535, grilling Q6).
      onConfirm: () =>
        logout.mutateAsync().then(
          () => {
            clearSession();
            navigate(routes.login);
          },
          () => undefined,
        ),
    });
  };

  const goFromMenu = (to: string) => {
    setProfileOpen(false);
    navigate(to);
  };

  // Change Password finished or its code lapsed: either way the Grower lands on the panel again.
  const backToProfilePanel = () => {
    setChangePasswordOpen(false);
    setProfileOpen(true);
  };

  const displayName = user ? `${user.firstName} ${user.lastName}` : '';

  // ADR-0023 / grilling Q6: the item navigates to the Profile FIRST and opens the panel over it, so
  // 113581 §B's "closing returns me to my Profile" holds literally. `/profile` is another story's
  // placeholder and is deliberately untouched; the panel lives here, in chrome that outlives every
  // route change, so it survives the navigation it triggers and keeps working when the real Profile
  // page lands.
  const [claimsOpen, setClaimsOpen] = useState(false);

  return (
    <header
      className={[styles.header, floating ? styles.floating : undefined].filter(Boolean).join(' ')}
    >
      {floating ? null : (
        <div className={styles.pattern} aria-hidden data-testid="top-nav-pattern" />
      )}
      <div className={styles.bar}>
        <nav className={styles.nav} aria-label="Primary">
          {nav.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) =>
                [styles.link, isActive ? styles.linkActive : undefined].filter(Boolean).join(' ')
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          className={styles.logoBtn}
          aria-label="Go to home"
          onClick={() => navigate(routes.home)}
        >
          <FieldProLogo tone="onDark" />
        </button>

        <div className={styles.actions}>
          {/*
            The per-page header slot. **No page fills it today**: the Map's search is an in-page
            input (ticket 15), the Devices listing has its own toolbar search, and Device Details'
            magnifier was removed on request — it was an icon with no handler behind it, so it read
            as a working control that did nothing. 113583 §1b does call for a search there; if it
            comes back it opts in through this slot with a real behaviour attached.
          */}
          <div className={styles.slot} ref={slotRef} />

          <Dropdown
            trigger={['click']}
            open={notificationsOpen}
            onOpenChange={setNotificationsOpen}
            placement="bottomRight"
            // antd keeps popup content mounted after close by default, which would leave the panel
            // alive — and marking rows read — while the grower is nowhere near it. Destroying it
            // makes each open a fresh mount: a fresh fetch, and read marked on what that open
            // actually listed (US-101849 § ENTRY POINT).
            destroyOnHidden
            // The panel owns its own card, padding and shadow, so antd's popup chrome would be a
            // second box around it.
            popupRender={() => <NotificationsPanel onClose={() => setNotificationsOpen(false)} />}
          >
            <button type="button" className={styles.iconBtn} aria-label="Notifications">
              <BellIcon size={24} />
              {hasUnread ? (
                <>
                  {count > 0 ? (
                    <span className={styles.unreadCount} aria-hidden>
                      {countLabel}
                    </span>
                  ) : (
                    <span className={styles.unreadDot} aria-hidden />
                  )}
                  {/* The badge is `aria-hidden` so the count is announced as a sentence rather
                      than as a bare number floating next to "Notifications". */}
                  <span className={styles.srOnly}>
                    {count > 0
                      ? `${count} unread notification${count === 1 ? '' : 's'}`
                      : 'Unread notifications'}
                  </span>
                </>
              ) : null}
            </button>
          </Dropdown>

          {user ? (
            <Dropdown
              trigger={['click']}
              open={profileOpen}
              onOpenChange={setProfileOpen}
              placement="bottomRight"
              // Each open is a fresh mount: a complete body is reused from the cache within its
              // staleTime, while a partial or failed one is refetched, toasting once per fetch
              // (US-117535, `useProfile`).
              destroyOnHidden
              popupRender={() => (
                <ProfileMenu
                  user={user}
                  onOpenNotificationSettings={() => {
                    setProfileOpen(false);
                    setSettingsOpen(true);
                  }}
                  onOpenWarrantyClaims={() => {
                    // Navigate first, then open over it — see the note on `claimsOpen`. The
                    // Profile-menu redesign gave this row a route; US-118754 gives it the
                    // account-wide panel, which is the behaviour its own story specifies.
                    goFromMenu(routes.profile);
                    setClaimsOpen(true);
                  }}
                  onOpenChangePassword={() => {
                    setProfileOpen(false);
                    setChangePasswordOpen(true);
                  }}
                  onOpenDevices={(status) => {
                    useDevicesUiStore.getState().showOnly(status);
                    goFromMenu(routes.devices);
                  }}
                  onOpenContactUs={() => {
                    setProfileOpen(false);
                    setContactOpen(true);
                  }}
                  onNavigate={goFromMenu}
                  onSignOut={signOut}
                  onEditProfile={(identity) => {
                    setProfileOpen(false);
                    setEditing(identity);
                  }}
                />
              )}
            >
              <button type="button" className={styles.profileBtn} aria-label="Account menu">
                {/* The brand's default avatar, shared with the mobile app, until profile photos
                    exist — the design shows an illustrated avatar here, not initials. */}
                <UserAvatar name={displayName} src={defaultAvatar} size={32} />
              </button>
            </Dropdown>
          ) : null}
        </div>
      </div>

      {/* Both mounted outside the dropdown: the menu that opens them unmounts on selection, and a
          drawer rendered inside a closing popover would be torn down with it. */}
      <NotificationSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {/* No `customerDeviceId` — that is what scopes the panel to the whole account (ADR-0023). */}
      <WarrantyClaimsDrawer open={claimsOpen} onClose={() => setClaimsOpen(false)} />
      {/* Mounted only while open: Cancel / ✕ discards every step's state with it (117548 BR 18).
          Done, and a lapsed code, re-open the Profile panel — "returned to the Profile section". */}
      {user && changePasswordOpen ? (
        <ChangePasswordFlow
          user={user}
          onClose={() => setChangePasswordOpen(false)}
          onDone={backToProfilePanel}
          onExpired={backToProfilePanel}
        />
      ) : null}
      <ContactUsModal open={contactOpen} onClose={() => setContactOpen(false)} />
      {editing ? <EditProfileModal identity={editing} onClose={() => setEditing(null)} /> : null}
    </header>
  );
}
